<template>
  <div class="SanitasTermsDetail">
    <div class="title">
      {{ title }}
    </div>

    <div class="version">
      {{ dateAndVersion }}
    </div>

    <div class="terms-outer" @scroll.once="hasScrolled = true">
      <div
        class="terms-detail-scroll arrow-animation arrow-container"
        :class="{ 'is-hidden': hasScrolled }"
      >
        <Icon
          class="terms-detail-scroll-icon"
          color="lightGrey"
          name="arrow-down"
        />
      </div>
      <div ref="termsInner" class="terms-inner">
        <div class="terms-content">
          <ul class="jumplink-list">
            <li
              v-for="(contentItem, i) in content"
              :key="i"
              class="jumplink"
              @click="followLocalLink('content_item_content_' + i)"
              @mouseenter="contentItem.hover = true"
              @mouseleave="contentItem.hover = false"
            >
              <Icon
                :color="contentItem.hover ? 'green' : 'lightGreen'"
                size="12"
                class="jumplink-icon"
                name="arrow-right-long"
              />
              <a class="link">
                {{ contentItem.title }}
              </a>
            </li>
          </ul>
          <div
            v-for="(item, i) in content"
            :id="'content_item_content_' + i"
            :key="'content_item_content_' + i"
          >
            <div class="terms-content-title">
              {{ item.title }}
            </div>
            <div class="terms-content-paragraph" v-html="item.content" />
          </div>
        </div>
      </div>

      <!-- Decline + accept buttons -->
      <div v-if="!readOnly" class="buttons">
        <sanitas-button
          secondary
          full-width-in-xs
          :is-spinner-active="pending"
          @click="$emit('decline')"
        >
          {{ label('terms_detail_decline_button_label') }}
        </sanitas-button>
        <sanitas-button
          class="accept"
          primary
          full-width-in-xs
          :is-spinner-active="pending"
          @click="$emit('accept')"
        >
          {{ label('terms_detail_accept_button_label') }}
        </sanitas-button>
      </div>
    </div>
  </div>
</template>

<script>
import { useI36n } from '@jota-one/i36n/src/index.vue2'
import { computed, ref } from '@vue/composition-api'
import { format as formatDate } from 'date-fns'

import Icon from './icon/Icon'
import SanitasButton from './SanitasButton'

export default {
  name: 'SanitasTermsDetail',

  components: {
    Icon,
    SanitasButton,
  },

  props: {
    title: {
      type: String,
      default: '',
    },
    version: {
      type: String,
      default: '',
    },
    date: {
      type: String,
      required: true,
    },
    content: {
      type: Array,
      default: () => [],
    },
    readOnly: {
      type: Boolean,
      default: true,
    },
    pending: Boolean,
  },

  setup(props) {
    const { $label: label } = useI36n()

    const termsInner = ref(null)
    const hasScrolled = ref(false)
    const dateAndVersion = computed(() =>
      label.value('terms_version_label', {
        version: props.version,
        date: formatDate(new Date(props.date), 'dd.MM.yyyy'),
      }),
    )

    const followLocalLink = targetId => {
      const blockEl = termsInner.value.querySelector('#' + targetId)
      blockEl.scrollIntoView(true)
    }

    return {
      label,
      dateAndVersion,
      followLocalLink,
      hasScrolled,
      termsInner,
    }
  },
}
</script>

<style lang="postcss">
.SanitasTermsDetail {
  position: relative;
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  padding: 30px;
  height: calc(100vh - 80px);

  @media (--md) {
    height: calc(80vh - 80px);
  }

  .title {
    font-family: var(--font-family-open-sans);
    font-weight: 400;
    font-size: 25px;
    line-height: 1.2;
    color: var(--c-black);
    margin-bottom: 7px;

    @media (min-width: 768px) {
      font-size: 24px;
    }
  }

  .version {
    font-size: 12px;
    margin-bottom: 20px;
  }

  .terms-detail-scroll {
    display: block;
    position: absolute;
    bottom: 30px;
    right: 10px;

    &.is-hidden {
      opacity: 0;
      transition: opacity 1s ease-in-out;
    }

    .terms-detail-scroll-icon {
      position: relative;
      right: 20px;
      top: 0;
    }
  }

  .arrow-animation {
    animation-name: animation-down, animation-updown;
    animation-duration: 0.3s, 1.5s;
    animation-delay: 0s, 0.3s;
    animation-timing-function: ease-out, ease-in-out;
    animation-iteration-count: 1, infinite;
    animation-fill-mode: forwards;
    animation-direction: normal, alternate;
  }

  .global-sanitas-category-teaser-text,
  .terms-detail-container .global-sanitas-category-teaser-text {
    color: var(--c-dark-grey);
    padding-left: 3px;
  }

  .terms-detail-separator-bottom {
    margin-top: 20px;

    @media (--md) {
      margin-left: 70px;
    }
  }

  .terms-outer {
    font-family: var(--font-family-open-sans);
    background-color: #f3f2f0;
    min-height: 100px;
    height: 100%;
    overflow: auto;

    .terms-inner {
      position: relative;

      .terms-content {
        font-size: 15px;
        font-style: normal;
        font-stretch: normal;
        letter-spacing: 0.2px;
        color: var(--c-dark-grey);
        padding: 10px;

        > div:first-of-type,
        > div:last-of-type {
          margin-top: 50px;
        }

        .terms-content-title {
          font-family: var(--font-family-open-sans);
          font-size: 15px;
          font-weight: bold;
          line-height: 1.33;
          margin: 21px 0 10px 0;
          color: var(--c-black);
        }

        .terms-content-paragraph p {
          font-family: var(--font-family-open-sans);
          font-size: 15px;
          line-height: 1.33;
          color: var(--c-black);
          padding-bottom: 15px;

          h1,
          h2,
          h3,
          h4,
          h5,
          h6,
          p {
            &:first-child {
              margin-top: 10px;
            }
          }
        }

        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
          margin: 21px 0 10px;
          font-family: var(--font-family-open-sans);
          font-size: 14px;
          line-height: 1.33;
          font-weight: bold;
          color: var(--c-dark-grey);

          & + h1,
          & + h2,
          & + h3,
          & + h4,
          & + h5,
          & + h6 {
            margin-top: 10px;
          }
        }
      }

      .terms-footer {
        .terms-footer-acknowledgement {
          margin-top: 10px;
          margin-right: 40px;
          width: 100% !important;
          font-family: 'Open Sans', sans-serif;
          font-size: 15px;
          font-weight: bold;
          line-height: 1.33;
          color: var(--c-black);
          margin-bottom: 80px;
        }

        .terms-footer-actions {
          button:nth-child(2) {
            float: right;
          }

          @media (max-width: 1024px) {
            height: 120px;

            button {
              width: 100%;
              clear: both;
            }

            button:nth-child(2) {
              margin-top: 20px;
            }
          }

          .global-sanitas-link {
            display: block;
            margin-left: 40px;
            margin-top: 10px;
          }
        }
      }
    }
  }

  .terms-detail-separator-bottom {
    display: none !important;
  }

  @media (min-width: 768px) {
    .terms-outer {
      .terms-inner {
        .terms-content {
          padding: 35px 50px 50px;
        }
      }
    }
  }

  .buttons {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin: 0 10px 20px;

    @media (min-width: 768px) {
      flex-direction: row;
      margin: 0 50px 50px;
    }

    .accept {
      margin-top: 20px;

      @media (min-width: 768px) {
        margin-top: 0;
      }
    }
  }
}

ul.jumplink-list {
  list-style: none;
  padding: 0;
  margin: 0;

  .jumplink {
    position: relative;
    font-size: 15px;
    line-height: normal;
    display: table;
    margin-bottom: 10px;

    .jumplink-icon {
      position: absolute;
      top: 2px;
    }

    &:hover {
      cursor: pointer;

      .link {
        color: var(--c-green);
        text-decoration: underline;
      }
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  .link {
    font-family: var(--font-family-open-sans);
    font-style: normal;
    font-size: 15px;
    letter-spacing: 0.25px;
    color: var(--c-light-green);
    cursor: pointer;
    transition: color 0.3s ease-in-out 0s;
    border-bottom: none; /* overwriting global leaking styles */
    text-decoration: none;
    padding-left: 23px;
    display: inline-block;
    font-weight: normal;
    line-height: normal;

    &:hover {
      color: var(--c-green);
      text-decoration: underline;
    }
  }
}

@keyframes animation-updown {
  0% {
    transform: translateY(6px);
  }
  50% {
    transform: translateY(2px);
  }
  100% {
    transform: translateY(6px);
  }
}

@keyframes animation-down {
  100% {
    transform: translateY(6px);
  }
}
</style>
